/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-79 — the sound a locked kiosk makes when an order is waiting
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * SYNTHESISED, not a file. Three reasons, in order of weight:
 *
 *  1. It must not be the sale chime. `/sounds/sale-confirmation.mp3` is the one
 *     cue florists have already learned and it means "money went in". Reusing
 *     it to mean "come and do some work" would overwrite a meaning that is
 *     already doing a job.
 *  2. No 50 KB binary enters the repo for a two-note ping, and nothing has to
 *     be sourced, licensed or committed.
 *  3. The tone is tunable in code — if it turns out to be wrong in a noisy
 *     shop, that is an edit, not a new asset.
 *
 * THE AUTOPLAY PROBLEM IS THE REAL PROBLEM. A browser refuses to make noise
 * until the page has been interacted with, and this screen's whole premise is
 * that NOBODY has interacted with it — possibly for hours. So the audio context
 * is created and resumed on any touch (`unlock`), long before there is anything
 * to announce, and reused thereafter. `SaleConfirmationOverlay` never had to
 * think about this because it only ever fires milliseconds after a tap.
 */

/** What `AlertChime` needs from the outside world, so tests can supply fakes. */
export interface TonePlayer {
  /** True when this environment can make a sound at all. */
  readonly supported: boolean;
  /** Called on a user gesture. Safe to call repeatedly. */
  unlock(): void;
  /** Make the noise. Never throws. */
  play(): void;
}

/** Timer seam — `window` does not exist under the node test environment. */
export interface AlertTimers {
  setInterval(handler: () => void, ms: number): number;
  clearInterval(id: number): void;
}

/**
 * Two rising notes: recognisably "attention", not a warning klaxon.
 *
 * PITCHED HIGH ON PURPOSE (owner, heard it on the tablet 2026-09-14: "sound
 * should be higher"). Human hearing is most sensitive around 2–4 kHz, and a
 * shop is full of low noise — fridges, traffic, conversation — so a low tone is
 * swallowed by the room while a high one carries to the back. These two sit
 * just under that band: audible from across the shop without being shrill at
 * arm's length, which matters when it repeats every minute.
 *
 * TO RETUNE: `hz` is the pitch, `GAIN_PEAK` below is the volume. They are
 * independent — ask for either.
 */
const NOTES: Array<{ hz: number; at: number; for: number }> = [
  { hz: 1568, at: 0, for: 0.16 }, // G6
  { hz: 2093, at: 0.14, for: 0.3 }, // C7
];

/** Peak loudness of each note, 0–1. Raised with the pitch, same report. */
const GAIN_PEAK = 0.45;

export function createWebAudioTonePlayer(): TonePlayer {
  const Ctor =
    typeof window === "undefined"
      ? undefined
      : window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;

  if (!Ctor) {
    return { supported: false, unlock: () => undefined, play: () => undefined };
  }

  let ctx: AudioContext | null = null;

  const ensure = (): AudioContext | null => {
    try {
      ctx ??= new Ctor();
      // Created suspended when there has been no gesture yet; resuming is
      // exactly what the gesture buys us.
      if (ctx.state === "suspended") void ctx.resume();
      return ctx;
    } catch {
      return null;
    }
  };

  return {
    supported: true,
    unlock: () => void ensure(),
    play: () => {
      const audio = ensure();
      if (!audio || audio.state !== "running") return;
      const start = audio.currentTime;
      for (const note of NOTES) {
        try {
          const osc = audio.createOscillator();
          const gain = audio.createGain();
          osc.type = "sine";
          osc.frequency.value = note.hz;
          // Shaped rather than square-edged: an abrupt start clicks.
          gain.gain.setValueAtTime(0.0001, start + note.at);
          gain.gain.exponentialRampToValueAtTime(GAIN_PEAK, start + note.at + 0.02);
          gain.gain.exponentialRampToValueAtTime(
            0.0001,
            start + note.at + note.for,
          );
          osc.connect(gain).connect(audio.destination);
          osc.start(start + note.at);
          osc.stop(start + note.at + note.for + 0.02);
        } catch {
          // One bad note must not take the rest of the chime down.
        }
      }
    },
  };
}

/**
 * What the chime should do this render, given the two things that drive it.
 *
 * EXTRACTED BECAUSE THE ORIGINAL BUG LIVED HERE, not inside `AlertChime`. The
 * defect was the wiring choice — driving arming AND sounding from a single
 * `waitingCount > 0` boolean, which does not change when the count goes 1 → 2,
 * so a second order arrived in silence. That decision was untestable while it
 * sat inside a React effect; as a function it is six lines and six cases.
 */
export interface ChimePlan {
  /** Keep the minute clock running. */
  arm: boolean;
  /** Stop everything — nothing is waiting, or nothing can be trusted. */
  silence: boolean;
  /** Make a noise now: something NEW turned up. */
  pulse: boolean;
  /** Drain the arrivals queue so the same order is never announced twice. */
  acknowledge: boolean;
}

export function planChime(active: boolean, arrivalCount: number): ChimePlan {
  if (!active) {
    return {
      arm: false,
      silence: true,
      // Still drain: arrivals left queued while inactive would fire the moment
      // work reappeared, announcing an order that was dealt with minutes ago.
      acknowledge: arrivalCount > 0,
      pulse: false,
    };
  }
  return {
    arm: true,
    silence: false,
    pulse: arrivalCount > 0,
    acknowledge: arrivalCount > 0,
  };
}

/** The slice of `HTMLAudioElement` we use, so tests need no DOM. */
export interface AudioElementLike {
  volume: number;
  currentTime: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: "error", listener: () => void): void;
}

/**
 * Plays a real recording, falling back to the synthesised chime.
 *
 * The owner picked a file (Pixabay, 2026-09-14) because a recorded bell cuts
 * through a noisy shop better than a sine tone. The synth stays as the fallback
 * rather than being deleted: if the asset 404s after a bad deploy, the kiosk
 * must still make a noise. A silent alert is indistinguishable from no order.
 *
 * PRIMED SILENTLY. An `Audio` element is as gesture-locked as an AudioContext,
 * so `unlock` plays it at volume 0 and immediately rewinds — that is what buys
 * permission to play later, when nobody is touching the tablet. Both players
 * are unlocked, because which one will be needed is not known until it fails.
 */
export function createFileTonePlayer(
  makeAudio: (() => AudioElementLike) | null,
  fallback: TonePlayer,
): TonePlayer {
  if (!makeAudio) return fallback;
  let element: AudioElementLike | null = null;
  let broken = false;
  /** Set once a silent prime has SUCCEEDED; after that unlock never touches it. */
  let primed = false;
  /** Guards two taps racing into the prime and fighting over `volume`. */
  let priming = false;

  const ensure = (): AudioElementLike | null => {
    if (broken) return null;
    if (!element) {
      try {
        element = makeAudio();
        element.addEventListener("error", () => {
          broken = true;
        });
      } catch {
        broken = true;
        return null;
      }
    }
    return element;
  };

  return {
    supported: true,
    unlock: () => {
      fallback.unlock();
      /*
       * ONCE, AND NEVER WHILE IT MAY BE PLAYING.
       *
       * This used to run on every tap, muting and pausing the very element the
       * alert plays through: a florist brushing the tablet mid-chime silenced
       * it, and a prime that resolved but failed to restore left every later
       * alert silent while the code reported success. Priming is a one-time
       * permission grab, so once it has worked there is no reason to touch the
       * element again — and every reason not to.
       *
       * `primed` is set only on SUCCESS, so a refused prime is retried by the
       * next gesture instead of leaving the element stuck at volume 0.
       */
      if (primed || priming) return;
      const audio = ensure();
      if (!audio) return;
      priming = true;
      audio.volume = 0;
      void audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = 1;
          primed = true;
        })
        .catch(() => {
          audio.volume = 1;
        })
        .finally(() => {
          priming = false;
        });
    },
    play: () => {
      const audio = ensure();
      if (!audio) {
        fallback.play();
        return;
      }
      try {
        audio.currentTime = 0;
        void audio.play().catch(() => fallback.play());
      } catch {
        fallback.play();
      }
    },
  };
}

/**
 * Plays once on arrival, then every `repeatMs` for as long as the work is still
 * waiting — the owner chose a 60-second repeat until somebody claims it
 * (2026-09-13), over my suggestion of escalating on age. Recorded in
 * FINALIZING.md with both sides.
 */
export class AlertChime {
  private timer: number | null = null;

  constructor(
    private readonly tone: TonePlayer,
    private readonly timers: AlertTimers,
    private readonly repeatMs: number,
  ) {}

  get supported(): boolean {
    return this.tone.supported;
  }

  /** Call from a user gesture, well before anything needs announcing. */
  unlock(): void {
    this.tone.unlock();
  }

  /**
   * Arm the minute repeat WITHOUT making a noise now.
   *
   * Silent on purpose: `pulse` owns every deliberate sound, including the one
   * for the first order. If `start` also played, the first order would be
   * announced twice — once by the arrival and once by the arming — which is
   * how the old `active`-flag version had to work and why a SECOND order was
   * announced not at all.
   *
   * Calling twice does not stack a second timer.
   */
  start(): void {
    if (this.timer !== null) return;
    this.timer = this.timers.setInterval(() => this.tone.play(), this.repeatMs);
  }

  /**
   * A new order arrived: sound NOW and restart the minute clock.
   *
   * Restarting matters. Without it a chime for a newly-arrived order could be
   * followed a second later by the repeat left over from the previous one, so
   * two orders a minute apart would sometimes double-ring and sometimes not.
   * The florist's rule stays simple: one sound per arrival, then one a minute
   * for as long as nobody takes it.
   */
  pulse(): void {
    // NOT ARMED MEANS NOTHING IS WAITING, so there is nothing to announce.
    // Without this guard a pulse from a late response, a retry, or a race where
    // arrivals outlive `active` would install a repeat that only an explicit
    // stop() could ever clear — a shop chiming every minute at a screen showing
    // no orders. The caller arms first; that ordering is now enforced here
    // rather than assumed.
    if (this.timer === null) return;
    this.tone.play();
    this.timers.clearInterval(this.timer);
    this.timer = this.timers.setInterval(() => this.tone.play(), this.repeatMs);
  }

  /** Silence. Safe when never started. */
  stop(): void {
    if (this.timer === null) return;
    this.timers.clearInterval(this.timer);
    this.timer = null;
  }

  get isSounding(): boolean {
    return this.timer !== null;
  }
}
